import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, addMonths, nextFriday, nextMonday, startOfDay } from "date-fns";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AdminCombobox } from "@/features/search/components/admin-combobox";
import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import adminAPI from "@/services/api";

/** Shape actually returned by GET /admin/admins (the SDK types it as object[]). */
interface AdminListItem {
  id: string;
  fullName: string;
  email?: string | null;
}

interface DatePreset {
  label: string;
  date: Date;
}

/** The 8 French date presets — mirrors CreateClaimTask.datePresets (Vue). */
function buildDatePresets(): DatePreset[] {
  const today = startOfDay(new Date());
  return [
    { label: "Demain", date: addDays(today, 1) },
    { label: "Dans 3 jours", date: addDays(today, 3) },
    { label: "Lundi prochain", date: nextMonday(today) },
    { label: "Vendredi prochain", date: nextFriday(today) },
    { label: "Dans 1 semaine", date: addDays(today, 7) },
    { label: "Dans 2 semaines", date: addDays(today, 14) },
    { label: "Dans 1 mois", date: addMonths(today, 1) },
    { label: "Dans 2 mois", date: addMonths(today, 2) },
  ];
}

interface CreateClaimTaskModalProps {
  claimId: string;
  open: boolean;
  onClose: () => void;
}

/** Port of Task/CreateClaimTask.vue — « Créer un rappel » depuis le menu Actions. */
export function CreateClaimTaskModal({ claimId, open, onClose }: CreateClaimTaskModalProps) {
  const { user: auth0User } = useAuth0();
  const queryClient = useQueryClient();

  const [taskTitle, setTaskTitle] = useState("");
  const [taskDueDate, setTaskDueDate] = useState<Date | null>(null);
  const [customDateTimeInput, setCustomDateTimeInput] = useState("");
  const [selectedPresetLabel, setSelectedPresetLabel] = useState<string | null>(null);
  const [taskAssignedAdminId, setTaskAssignedAdminId] = useState<string | null>(null);
  const [taskCategoryId, setTaskCategoryId] = useState<number | null>(1); // Default to "Autres"
  const [sendSlackNotification, setSendSlackNotification] = useState(true);

  const { data: adminList = [] } = useQuery({
    queryKey: queryKeys.search.admins,
    queryFn: async () => (await adminAPI.listAdmin()) as AdminListItem[],
  });

  const { data: categories = [] } = useQuery({
    queryKey: queryKeys.tasks.categories,
    queryFn: () => adminAPI.listTaskCategories(),
  });

  const currentUserEmail = auth0User?.email?.toLowerCase();
  const currentUserAdmin = currentUserEmail
    ? (adminList.find((admin) => admin.email?.toLowerCase() === currentUserEmail) ?? null)
    : null;
  // Default to the current user (mirrors the Vue open/adminList watchers)
  const effectiveAssignedAdminId = taskAssignedAdminId ?? currentUserAdmin?.id ?? null;

  const createTask = useMutation({
    mutationFn: (payload: Parameters<typeof adminAPI.upsertTask>[1]) =>
      notify.promise(adminAPI.upsertTask(claimId, payload), {
        loading: "Création du rappel…",
        success: "Rappel créé avec succès",
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) }),
      ]);
    },
  });

  const canSubmit =
    taskTitle.trim().length > 0 &&
    taskDueDate !== null &&
    effectiveAssignedAdminId !== null &&
    !createTask.isPending;

  const datePresets = buildDatePresets();

  function selectDatePreset(preset: DatePreset) {
    setTaskDueDate(preset.date);
    setSelectedPresetLabel(preset.label);
    setCustomDateTimeInput("");
  }

  function onCustomDateTimeChange(value: string) {
    setCustomDateTimeInput(value);
    if (value) {
      setTaskDueDate(new Date(value));
      setSelectedPresetLabel(null);
    }
  }

  function resetForm() {
    setTaskTitle("");
    setTaskDueDate(null);
    setCustomDateTimeInput("");
    setSelectedPresetLabel(null);
    setTaskAssignedAdminId(null);
    setTaskCategoryId(1);
    setSendSlackNotification(true);
  }

  function close() {
    resetForm();
    onClose();
  }

  function createClaimTask() {
    if (!canSubmit || !taskDueDate || !effectiveAssignedAdminId) {
      return;
    }

    createTask.mutate(
      {
        assignedAdminId: effectiveAssignedAdminId,
        title: taskTitle,
        dueDate: taskDueDate,
        // Always SLACK_REMINDER for now (Vue parity)
        type: "SLACK_REMINDER",
        categoryId: taskCategoryId || 1,
      },
      {
        onSuccess: () => close(),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          close();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Créer un rappel</DialogTitle>
        </DialogHeader>

        <DialogPanel>
          <div className="space-y-6">
            <div>
              <Label htmlFor="task-title" className="block text-sm font-medium text-gray-900">
                Description du rappel
              </Label>
              <Input
                id="task-title"
                className="mt-2"
                placeholder="Ex: Relancer le client"
                value={taskTitle}
                onChange={(event) => setTaskTitle(event.target.value)}
              />
            </div>

            {/* biome-ignore lint/a11y/useSemanticElements: mirrors the Vue markup */}
            <div role="group" aria-labelledby="reminder-date-label">
              <h3 id="reminder-date-label" className="mb-3 text-sm font-semibold text-gray-900">
                Date du rappel
              </h3>
              {/* biome-ignore lint/a11y/useSemanticElements: mirrors the Vue markup */}
              <div className="grid grid-cols-2 gap-2" role="group">
                {datePresets.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => selectDatePreset(preset)}
                    aria-pressed={selectedPresetLabel === preset.label}
                    aria-label={`Programmer pour ${preset.label.toLowerCase()}`}
                    className={cn(
                      "rounded-md border px-3 py-2 text-left text-sm font-medium transition-colors",
                      selectedPresetLabel === preset.label
                        ? "border-blue-600 bg-blue-50 text-blue-700 ring-1 ring-blue-600"
                        : "border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50",
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                <Label
                  htmlFor="custom-datetime-task"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Ou choisir une date et heure précise
                </Label>
                <input
                  id="custom-datetime-task"
                  type="datetime-local"
                  value={customDateTimeInput}
                  onChange={(event) => onCustomDateTimeChange(event.target.value)}
                  className="block w-full rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            <AdminCombobox
              value={effectiveAssignedAdminId}
              onChange={setTaskAssignedAdminId}
              label="Assigner à"
              placeholder="Sélectionner un admin"
            />

            <div>
              <Label className="block text-sm font-medium text-gray-900">Catégorie</Label>
              <Select
                value={taskCategoryId !== null ? String(taskCategoryId) : null}
                onValueChange={(value) => setTaskCategoryId(value ? Number(value) : null)}
              >
                <SelectTrigger className="mt-2 w-full">
                  <SelectValue>
                    {categories.find((category) => category.id === taskCategoryId)?.displayName ??
                      "Sélectionner une catégorie"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={String(category.id)}>
                      {category.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={sendSlackNotification} onCheckedChange={setSendSlackNotification} />
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Recevoir un rappel sur</span>
                <img
                  src="https://ucarecdn.com/5d44d1d4-d8f6-4ce3-9d44-1e8adfaab88f/Slack_RGB.png"
                  alt="Slack"
                  className="-mx-3 h-10 object-contain"
                />
              </div>
            </div>
          </div>
        </DialogPanel>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Annuler
          </Button>
          <Button onClick={createClaimTask} disabled={!canSubmit}>
            Créer le rappel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
